import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import CommunityBanner from '../components/CommunityBanner';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: 'easeOut' },
  }),
};

const services = [
  {
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    title: 'Mechanical Repairs',
    desc: 'From minor fixes to major overhauls, our experienced team resolves issues efficiently to get you back on the road.',
  },
  {
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    title: 'Warrant of Fitness',
    desc: 'Comprehensive WOF inspections by certified technicians. We tell you exactly what your vehicle needs to pass.',
  },
  {
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    ),
    title: 'Vehicle Servicing',
    desc: 'Regular servicing keeps your vehicle running at its best. We follow manufacturer guidelines and use quality parts.',
  },
  {
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
      </svg>
    ),
    title: 'Diagnostics',
    desc: 'Modern vehicles need modern tools. We use the latest diagnostic equipment to identify issues quickly and accurately.',
  },
  {
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="9" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="12" cy="12" r="4" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v2m0 14v2M3 12h2m14 0h2" />
      </svg>
    ),
    title: 'Tyres and Balancing',
    desc: 'Supply and fitting of quality tyres for all vehicles and budgets, plus precision balancing using our Hunter SmartWeight Pro system.',
  },
  {
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
      </svg>
    ),
    title: 'Wheel Alignment',
    desc: 'Proper alignment extends tyre life and improves handling. Fast, precise alignment using our Fasep Italian machine.',
  },
];

const reviews = [
  {
    name: 'Sarah Haworth',
    text: 'Had these guys service my car today and put it through a WOF. Great service and good price. Would recommend, helpful, friendly and they dropped my car back off at work for me.',
    stars: 5,
  },
  {
    name: 'Mike Cooksley',
    text: 'Really great mechanics, friendly helpful team including reception. Have used these guys for both work and personal vehicles for years. Highly recommend.',
    stars: 5,
  },
  {
    name: 'Virginia Green',
    text: "Called into Auto Doctor because our engine light came on saying we'd overfilled our diesel engine with oil. Mike was fantastic and sucked out the excess. Thanks Mike!",
    stars: 5,
  },
];

function StarRating({ count }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: count }).map((_, i) => (
        <svg key={i} className="w-4 h-4" style={{ fill: '#CC0000' }} viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section className="relative flex items-end" style={{ minHeight: '100vh' }}>
        {/* Hero background image */}
        <img
          src="/exterior.jpg"
          alt="Auto Doctor workshop exterior, 22 Parata Street Waikanae"
          className="absolute inset-0 w-full h-full"
          style={{ objectFit: 'cover', objectPosition: 'center' }}
        />

        {/* Dark overlay at 55% opacity */}
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }} />
        {/* Bottom gradient to anchor content card */}
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(to top, rgba(10,15,28,0.7) 0%, transparent 60%)' }}
        />

        {/* Content card anchored bottom left */}
        <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 pb-16 pt-24">
          <motion.div
            className="max-w-xl rounded-2xl p-8 sm:p-10"
            style={{ backgroundColor: 'rgba(10,15,28,0.85)', border: '1px solid rgba(204,0,0,0.3)', backdropFilter: 'blur(8px)' }}
            initial="hidden"
            animate="visible"
            variants={fadeUp}
          >
            <motion.p
              className="text-xs font-semibold uppercase tracking-widest mb-3"
              style={{ color: '#CC0000' }}
              custom={0}
              variants={fadeUp}
            >
              Kapiti Coast Mechanics
            </motion.p>
            <motion.h1
              className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-4"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              custom={1}
              variants={fadeUp}
            >
              Waikanae's Trusted Mechanical Experts
            </motion.h1>
            <motion.p className="text-gray-300 text-base sm:text-lg leading-relaxed mb-8" custom={2} variants={fadeUp}>
              Full mechanical repairs, WOF, servicing, diagnostics, tyres and wheel alignment. Proudly serving the Kapiti Coast.
            </motion.p>
            <motion.div className="flex flex-col sm:flex-row gap-3" custom={3} variants={fadeUp}>
              <Link
                to="/contact"
                className="inline-block px-7 py-3 text-base font-semibold text-white text-center rounded transition-all duration-200"
                style={{ backgroundColor: '#CC0000' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#AA0000'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#CC0000'; }}
              >
                Book a Service
              </Link>
              <Link
                to="/services"
                className="inline-block px-7 py-3 text-base font-semibold text-white text-center rounded border-2 transition-all duration-200"
                style={{ borderColor: '#FFFFFF', backgroundColor: 'transparent' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#CC0000'; e.currentTarget.style.color = '#CC0000'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#FFFFFF'; e.currentTarget.style.color = '#FFFFFF'; }}
              >
                Our Services
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section style={{ backgroundColor: '#070C18', borderTop: '1px solid rgba(204,0,0,0.2)', borderBottom: '1px solid rgba(204,0,0,0.2)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { stat: '5.0 Stars', sub: '60+ Google Reviews' },
              { stat: '100% Recommended', sub: 'on Facebook' },
              { stat: 'Kapiti Coast', sub: 'Locals' },
            ].map((item, i) => (
              <motion.div
                key={i}
                className="text-center py-4"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={fadeUp}
              >
                <p className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#CC0000' }}>
                  {item.stat}
                </p>
                <p className="text-gray-400 text-sm mt-1">{item.sub}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#0A0F1C' }}>
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-14"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: '#CC0000' }}>What We Do</p>
            <h2
              className="text-4xl sm:text-5xl font-bold text-white"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Our Services
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((svc, i) => (
              <motion.div
                key={i}
                className="rounded-xl p-7 cursor-default"
                style={{ backgroundColor: '#0D1220', border: '1px solid rgba(255,255,255,0.06)' }}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={fadeUp}
                whileHover={{ y: -6, borderColor: 'rgba(204,0,0,0.5)', boxShadow: '0 8px 32px rgba(204,0,0,0.15)' }}
                transition={{ duration: 0.25 }}
              >
                <div className="mb-4" style={{ color: '#CC0000' }}>{svc.icon}</div>
                <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  {svc.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">{svc.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="text-center mt-10"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <Link
              to="/services"
              className="inline-block px-8 py-3 font-semibold text-white rounded border-2 transition-all duration-200"
              style={{ borderColor: '#CC0000', backgroundColor: 'transparent' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#CC0000'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              View All Services
            </Link>
          </motion.div>
        </div>
      </section>

      {/* COMMUNITY */}
      <CommunityBanner />

      {/* ABOUT TEASER */}
      <section style={{ backgroundColor: '#F5F5F5' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: '#CC0000' }}>About Us</p>
            <h2
              className="text-4xl sm:text-5xl font-bold mb-6"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#0A0F1C' }}
            >
              Your Local Kapiti Coast Mechanics
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              Auto Doctor Ltd has been a trusted fixture in Waikanae for over 20 years, delivering honest, quality mechanical services to the Kapiti Coast community. Our experienced team takes pride in straightforward advice and workmanship you can rely on.
            </p>
            <Link
              to="/about"
              className="inline-block px-8 py-3 font-semibold text-white rounded transition-all duration-200"
              style={{ backgroundColor: '#CC0000' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#AA0000'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#CC0000'; }}
            >
              Meet the Team
            </Link>
          </motion.div>
        </div>
      </section>

      {/* REVIEW STRIP */}
      <section style={{ backgroundColor: '#0A0F1C' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: '#CC0000' }}>Google Reviews</p>
            <h2
              className="text-4xl sm:text-5xl font-bold text-white"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              What Our Customers Say
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {reviews.map((review, i) => (
              <motion.div
                key={i}
                className="rounded-xl p-7"
                style={{ backgroundColor: '#0D1220', border: '1px solid rgba(204,0,0,0.2)' }}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={fadeUp}
              >
                <StarRating count={review.stars} />
                <p className="text-gray-300 text-sm leading-relaxed mt-4 mb-5 italic">"{review.text}"</p>
                <p className="text-white font-semibold text-sm">{review.name}</p>
                <p className="text-xs text-gray-500 mt-0.5">Google Review</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="text-center mt-10"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <Link
              to="/testimonials"
              className="inline-block px-8 py-3 font-semibold text-white rounded border-2 transition-all duration-200"
              style={{ borderColor: '#CC0000' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#CC0000'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              See All Reviews
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
